import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Filter, X, Check } from 'lucide-react'
import { AutocompleteInput } from '../ui/AutocompleteInput'
import { useApi } from '../../hooks/useApi'
import { getNoticeTypeLabel } from '../../utils/formatters'
import { encodeFiltersToUrl, decodeFiltersFromUrl } from '../../utils/urlHelpers'

interface FiltersProps {
  // Add props interface if needed
}

interface DictionaryItem {
  id: string
  name: string
}

export default function Filters(_props: FiltersProps) {
  const { getOrganizationSuggestions, getCPVSuggestions, getDictionaryItems } = useApi()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [dictionaryOptions, setDictionaryOptions] = useState<{
    client_type: DictionaryItem[]
    order_type: DictionaryItem[]
    tender_type: DictionaryItem[]
    province: DictionaryItem[]
  }>({
    client_type: [],
    order_type: [],
    tender_type: [],
    province: [],
  })

  // Load dictionaries on mount
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const [clientTypes, orderTypes, tenderTypes, provinces] = await Promise.all([
          getDictionaryItems('client_type'),
          getDictionaryItems('order_type'),
          getDictionaryItems('tender_type'),
          getDictionaryItems('province'),
        ])
        setDictionaryOptions({
          client_type: clientTypes,
          order_type: orderTypes,
          tender_type: tenderTypes,
          province: provinces,
        })
      } catch (err) {
        console.error('Failed to load dictionaries', err)
      }
    }
    loadDictionaries()
  }, [getDictionaryItems])

  // Initialize filters from URL
  const [filters, setFilters] = useState(() => {
    return decodeFiltersFromUrl(searchParams.toString(), {
      order_object: '',
      organization_name: '',
      cpv_code: '',
      notice_types: ['ContractNotice', 'TenderResultNotice'], // Default notice types
      // Advanced filters
      organization_city: '',
      organization_province: '',
      client_type: '',
      order_type: '',
      tender_type: '',
      is_tender_amount_below_eu: null,
      procedure_result: '',
      bzp_number: '',
      date_from: '',
      date_to: '',
    })
  })

  const [isExpanded, setIsExpanded] = useState(false)
  const [isNoticeTypeDropdownOpen, setIsNoticeTypeDropdownOpen] = useState(false)
  const noticeTypeDropdownRef = useRef<HTMLDivElement>(null)

  // Click outside handler for notice type dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        noticeTypeDropdownRef.current &&
        !noticeTypeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsNoticeTypeDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Available notice types
  const availableNoticeTypes = [
    'ContractNotice',
    'TenderResultNotice',
    'AgreementIntentionNotice',
    'CompetitionNotice',
    'CompetitionResultNotice',
    'NoticeUpdateNotice',
    'AgreementUpdateNotice',
    'ContractPerformingNotice',
    'CircumstancesFulfillmentNotice',
    'SmallContractNotice',
    'ConcessionNotice',
    'ConcessionIntentionAgreementNotice',
    'NoticeUpdateConcession',
    'ConcessionAgreementNotice',
    'ConcessionUpdateAgreementNotice',
  ]

  const updateFiltersInUrl = (newFilters: any) => {
    const queryString = encodeFiltersToUrl(newFilters)
    setSearchParams(queryString ? { ...Object.fromEntries(new URLSearchParams(queryString)) } : {})
  }

  const handleInputChange = (field: string, value: any) => {
    const newFilters = { ...filters, [field]: value }
    setFilters(newFilters)
    updateFiltersInUrl(newFilters)
  }

  const handleNoticeTypeToggle = (noticeType: string) => {
    const currentTypes = filters.notice_types || []
    let newTypes

    if (currentTypes.includes(noticeType)) {
      // Remove the type
      newTypes = currentTypes.filter((type: string) => type !== noticeType)
    } else {
      // Add the type
      newTypes = [...currentTypes, noticeType]
    }

    handleInputChange('notice_types', newTypes)
  }

  const clearFilters = () => {
    const emptyFilters = {
      order_object: '',
      organization_name: '',
      cpv_code: '',
      notice_types: ['ContractNotice', 'TenderResultNotice'], // Reset to defaults
      organization_city: '',
      organization_province: '',
      client_type: '',
      order_type: '',
      tender_type: '',
      is_tender_amount_below_eu: null,
      procedure_result: '',
      bzp_number: '',
      date_from: '',
      date_to: '',
    }
    setFilters(emptyFilters)
    updateFiltersInUrl(emptyFilters)
  }

  const hasActiveFilters = () => {
    return (
      filters.order_object.trim() !== '' ||
      filters.organization_name.trim() !== '' ||
      filters.cpv_code.trim() !== '' ||
      filters.organization_city.trim() !== '' ||
      filters.organization_province.trim() !== '' ||
      filters.client_type.trim() !== '' ||
      filters.order_type.trim() !== '' ||
      filters.tender_type.trim() !== '' ||
      filters.is_tender_amount_below_eu ||
      filters.procedure_result.trim() !== '' ||
      filters.bzp_number.trim() !== '' ||
      filters.date_from.trim() !== '' ||
      filters.date_to.trim() !== ''
    )
  }

  const hasAnyFilters = () => {
    return hasActiveFilters() || (filters.notice_types && filters.notice_types.length > 0)
  }

  return (
    <div className="bg-surface-raised border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-ink-muted" />
          <h3 className="text-lg font-medium text-ink">Filtry wyszukiwania</h3>
          {hasActiveFilters() && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
              Aktywne filtry
            </span>
          )}
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-ink-muted hover:text-ink-secondary"
        >
          {isExpanded ? 'Zwiń' : 'Rozwiń'}
        </button>
      </div>

      {/* Basic filters: order object, organization, CPV */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        {/* Order Object */}
        <div>
          <label htmlFor="order-object" className="block text-sm font-medium text-ink-secondary mb-1">
            Przedmiot zamówienia
          </label>
          <input
            type="text"
            id="order-object"
            value={filters.order_object}
            onChange={(e) => handleInputChange('order_object', e.target.value)}
            placeholder="Słowa kluczowe..."
            className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
          />
        </div>

        {/* Organization Name - with autocomplete */}
        <div>
          <label
            htmlFor="organization-name"
            className="block text-sm font-medium text-ink-secondary mb-1"
          >
            Nazwa zamawiającego
          </label>
          <AutocompleteInput
            id="organization-name"
            value={filters.organization_name}
            onChange={(value) => handleInputChange('organization_name', value)}
            onSuggestionsFetch={getOrganizationSuggestions}
            placeholder="Wpisz nazwę zamawiającego..."
          />
        </div>

        {/* CPV Code - with autocomplete */}
        <div>
          <label htmlFor="cpv-code" className="block text-sm font-medium text-ink-secondary mb-1">
            Kod CPV
          </label>
          <AutocompleteInput
            id="cpv-code"
            value={filters.cpv_code}
            onChange={(value) => handleInputChange('cpv_code', value)}
            onSuggestionsFetch={getCPVSuggestions}
            placeholder="np. 45000000"
          />
        </div>
      </div>

      {/* Notice Type Filter */}
      <div className="mb-4">
{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className="block text-sm font-medium text-ink-secondary mb-2">Typ ogłoszenia</label>
        <div className="relative" ref={noticeTypeDropdownRef}>
          <button
            type="button"
            onClick={() => setIsNoticeTypeDropdownOpen(!isNoticeTypeDropdownOpen)}
            className="w-full px-3 py-2 text-left border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand bg-surface-raised"
            aria-expanded={isNoticeTypeDropdownOpen}
            aria-controls="notice-type-menu"
          >
            <div className="flex flex-wrap gap-1">
              {(filters.notice_types || []).length > 0 ? (
                (filters.notice_types || []).map((type: string) => (
                  <span
                    key={type}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand"
                  >
                    {getNoticeTypeLabel(type)}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleNoticeTypeToggle(type)
                      }}
                      className="ml-1 text-brand hover:text-brand cursor-pointer"
                      aria-label={`Usuń typ ${getNoticeTypeLabel(type)}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              ) : (
                <span className="text-ink-muted">Wybierz typy ogłoszeń...</span>
              )}
            </div>
          </button>

          {isNoticeTypeDropdownOpen && (
            <div
              id="notice-type-menu"
              className="absolute z-10 mt-1 w-full bg-surface-raised border border-border rounded-md max-h-60 overflow-y-auto"
            >
              {availableNoticeTypes.map((type) => {
                const isSelected = (filters.notice_types || []).includes(type)
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handleNoticeTypeToggle(type)}
                    className="w-full px-3 py-2 text-left hover:bg-surface-hover focus:bg-surface-hover focus:outline-none flex items-center"
                  >
                    <div className="flex items-center">
                      <div
                        className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${
                          isSelected ? 'bg-brand border-brand' : 'border-border'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-sm">{getNoticeTypeLabel(type)}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      {isExpanded && (
        <div className="border-t border-border pt-4 mb-4">
          <h4 className="text-md font-medium text-ink mb-3">Zaawansowane filtry</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label
                htmlFor="organization-city"
                className="block text-sm font-medium text-ink-secondary mb-1"
              >
                Miasto zamawiającego
              </label>
              <input
                type="text"
                id="organization-city"
                value={filters.organization_city}
                onChange={(e) => handleInputChange('organization_city', e.target.value)}
                placeholder="np. Warszawa"
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>

            <div>
              <label
                htmlFor="organization-province"
                className="block text-sm font-medium text-ink-secondary mb-1"
              >
                Województwo zamawiającego
              </label>
              <select
                id="organization-province"
                value={filters.organization_province}
                onChange={(e) => handleInputChange('organization_province', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand bg-surface-raised"
              >
                <option value="">Wszystkie</option>
                {dictionaryOptions.province.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="client-type" className="block text-sm font-medium text-ink-secondary mb-1">
                Typ klienta
              </label>
              <select
                id="client-type"
                value={filters.client_type}
                onChange={(e) => handleInputChange('client_type', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand bg-surface-raised"
              >
                <option value="">Wszystkie</option>
                {dictionaryOptions.client_type.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="order-type" className="block text-sm font-medium text-ink-secondary mb-1">
                Typ zamówienia
              </label>
              <select
                id="order-type"
                value={filters.order_type}
                onChange={(e) => handleInputChange('order_type', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand bg-surface-raised"
              >
                <option value="">Wszystkie</option>
                {dictionaryOptions.order_type.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="tender-type" className="block text-sm font-medium text-ink-secondary mb-1">
                Typ postępowania
              </label>
              <select
                id="tender-type"
                value={filters.tender_type}
                onChange={(e) => handleInputChange('tender_type', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand bg-surface-raised"
              >
                <option value="">Wszystkie</option>
                {dictionaryOptions.tender_type.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="tender-amount-below-eu"
                checked={filters.is_tender_amount_below_eu === true}
                onChange={(e) =>
                  handleInputChange('is_tender_amount_below_eu', e.target.checked ? true : null)
                }
                className="rounded border-border text-brand focus:ring-brand"
              />
              <label htmlFor="tender-amount-below-eu" className="text-sm font-medium text-ink-secondary">
                Kwota poniżej progu UE
              </label>
            </div>

            <div>
              <label
                htmlFor="procedure-result"
                className="block text-sm font-medium text-ink-secondary mb-1"
              >
                Wynik postępowania
              </label>
              <input
                type="text"
                id="procedure-result"
                value={filters.procedure_result}
                onChange={(e) => handleInputChange('procedure_result', e.target.value)}
                placeholder="np. awarded"
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>

            <div>
              <label htmlFor="bzp-number" className="block text-sm font-medium text-ink-secondary mb-1">
                Numer BZP
              </label>
              <input
                type="text"
                id="bzp-number"
                value={filters.bzp_number}
                onChange={(e) => handleInputChange('bzp_number', e.target.value)}
                placeholder="np. BZ-2025-001"
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>

            <div>
              <label htmlFor="date-from" className="block text-sm font-medium text-ink-secondary mb-1">
                Data od
              </label>
              <input
                type="date"
                id="date-from"
                value={filters.date_from}
                onChange={(e) => handleInputChange('date_from', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>

            <div>
              <label htmlFor="date-to" className="block text-sm font-medium text-ink-secondary mb-1">
                Data do
              </label>
              <input
                type="date"
                id="date-to"
                value={filters.date_to}
                onChange={(e) => handleInputChange('date_to', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-brand focus:border-brand"
              />
            </div>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {!isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="text-sm text-brand hover:text-brand font-medium"
            >
              Zaawansowane filtry
            </button>
          )}

          {hasActiveFilters() && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-surface-inset text-ink hover:bg-surface-inset"
            >
              <X className="h-4 w-4 mr-1" />
              Wyczyść filtry
            </button>
          )}
        </div>

        <div className="text-sm text-ink-muted">
          {hasActiveFilters() ? 'Filtry aktywne' : 'Brak aktywnych filtrów'}
        </div>
      </div>

      {/* Filter summary */}
      {hasAnyFilters() && (
        <div className="mt-4 pt-4 border-t border-border" data-testid="filter-summary">
          <div className="flex flex-wrap gap-2">
            {(filters.notice_types || []).length > 0 && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
                Typy ogłoszeń:{' '}
                {(filters.notice_types || []).map((type: string) => getNoticeTypeLabel(type)).join(', ')}
              </span>
            )}
            {filters.order_object && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning/10 text-warning">
                Przedmiot: {filters.order_object}
              </span>
            )}
            {filters.organization_name && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                Zamawiający: {filters.organization_name}
              </span>
            )}
            {filters.cpv_code && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai">
                CPV: {filters.cpv_code}
              </span>
            )}
            {filters.organization_city && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai">
                Miasto: {filters.organization_city}
              </span>
            )}
            {filters.organization_province && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ai/10 text-ai">
                Województwo:{' '}
                {dictionaryOptions.province.find((i) => i.id === filters.organization_province)
                  ?.name || filters.organization_province}
              </span>
            )}
            {filters.client_type && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
                Typ klienta:{' '}
                {dictionaryOptions.client_type.find((i) => i.id === filters.client_type)?.name ||
                  filters.client_type}
              </span>
            )}
            {filters.order_type && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
                Typ zamówienia:{' '}
                {dictionaryOptions.order_type.find((i) => i.id === filters.order_type)?.name ||
                  filters.order_type}
              </span>
            )}
            {filters.tender_type && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
                Typ postępowania:{' '}
                {dictionaryOptions.tender_type.find((i) => i.id === filters.tender_type)?.name ||
                  filters.tender_type}
              </span>
            )}
            {filters.is_tender_amount_below_eu && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-bg text-brand">
                Poniżej progu UE
              </span>
            )}
            {filters.date_from && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-danger/10 text-danger">
                Od: {filters.date_from}
              </span>
            )}
            {filters.date_to && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-danger/10 text-danger">
                Do: {filters.date_to}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
